/* eslint-disable ember/no-computed-properties-in-native-classes */
// Extends From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  filterBy,
  findBy,
  rejectBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class StudentController extends Controller {
  @service
  session;

  @tracked autoinjectorsOnly = false;
  @tracked
  filters = [];
  @tracked showDiscontinued = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get isNSWSchool() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @computed(
    'model.medications.@each.{isAutoinjector,deleted}',
    'autoinjectorsOnly',
    'showDiscontinued',
  )
  get medications() {
    let medications = this.model.medications;
    if (this.autoinjectorsOnly) {
      medications = filterBy(medications, 'isAutoinjector');
    }
    if (!this.showDiscontinued) {
      medications = rejectBy(medications, 'deleted');
    }
    return medications;
  }

  @computed('medications.[]', 'filters.@each.{field,value}')
  get filteredMedications() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.medications;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.medications.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
